import React, { useState, useEffect, useCallback, useMemo, memo } from "react";
import { ClipLoader } from "react-spinners";
import { useGetAllSignLinkDocumentsQuery } from "../services/useGetAllSignLinkDocumentsQuery";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faSort,
  faSortUp,
  faSortDown,
} from "@fortawesome/free-solid-svg-icons";
import Cookies from "js-cookie";
import { useNavigate } from "react-router-dom";
import PropTypes from "prop-types";

const Badge = memo(({ text, bgColor, textColor }) => (
  <span
    className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${bgColor} ${textColor}`}
  >
    {text}
  </span>
));

Badge.propTypes = {
  text: PropTypes.string.isRequired,
  bgColor: PropTypes.string.isRequired,
  textColor: PropTypes.string.isRequired,
};

const DocumentTypeFilter = memo(({ documentType, setDocumentType }) => {
  const documentTypes = [
    { id: 1, label: "GFE" },
    { id: 2, label: "RX" },
    { id: 3, label: "Collab" },
    { id: 4, label: "Template Sign" },
    { id: 5, label: "Operator Sign" },
  ];

  return (
    <div className="flex space-x-4 mb-4">
      {documentTypes.map(({ id, label }) => (
        <button
          key={id}
          onClick={() => setDocumentType(id)}
          className={`px-4 py-2 rounded-lg ${
            documentType === id
              ? "bg-[#cfc9ff] text-black"
              : "bg-gray-200 text-gray-700 hover:bg-gray-300"
          }`}
        >
          {label}
        </button>
      ))}
    </div>
  );
});

DocumentTypeFilter.propTypes = {
  documentType: PropTypes.number.isRequired,
  setDocumentType: PropTypes.func.isRequired,
};

const TableHeader = memo(({ sortColumn, sortDirection, handleSort }) => {
  const columns = [
    { key: "qualiphy_clinic_id", label: "Clinic" },
    { key: "qualiphy_admin_id", label: "Admin" },
    { key: "qualiphy_user_id", label: "User" },
    { key: "is_active", label: "Active" },
    { key: "qualiphy_clinic_name", label: "Clinic Name" },
    { key: "document_type", label: "Type" },
    { key: "status", label: "Status" },
    { key: "date_created", label: "Date" },
  ];

  const getSortIcon = (column) => {
    if (column === sortColumn) {
      return sortDirection === "asc" ? faSortUp : faSortDown;
    }
    return faSort;
  };

  return (
    <thead className="text-xs text-gray-700 uppercase bg-gray-50 dark:bg-gray-700 dark:text-gray-400">
      <tr>
        {columns.map(({ key, label }) => (
          <th
            key={key}
            scope="col"
            className="py-2 px-3 sm:px-2 cursor-pointer"
            onClick={() => handleSort(key)}
          >
            {label}
            <FontAwesomeIcon icon={getSortIcon(key)} className="ml-1" />
          </th>
        ))}
        <th scope="col" className="py-2 px-3 sm:px-2">
          Final Document
        </th>
      </tr>
    </thead>
  );
});

TableHeader.propTypes = {
  sortColumn: PropTypes.string,
  sortDirection: PropTypes.string.isRequired,
  handleSort: PropTypes.func.isRequired,
};

const Pagination = memo(({ currentPage, totalPages, onPageChange }) => {
  const renderPageButtons = () => {
    return Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
      const pageNumber =
        Math.max(1, Math.min(totalPages - 4, currentPage - 2)) + i;
      return (
        <button
          key={pageNumber}
          onClick={() => onPageChange(pageNumber)}
          className={`px-4 py-2 mx-1 rounded ${
            currentPage === pageNumber
              ? "bg-[#cfc9ff] text-black hover:bg-[#948ae3]"
              : "bg-gray-200 hover:bg-gray-300"
          }`}
        >
          {pageNumber}
        </button>
      );
    });
  };

  return (
    <div className="flex items-center">
      <button
        onClick={() => onPageChange(currentPage - 1)}
        disabled={currentPage === 1}
        className="px-4 py-2 mx-1 bg-gray-200 hover:bg-gray-300 disabled:opacity-50 rounded"
      >
        Previous
      </button>
      {renderPageButtons()}
      <button
        onClick={() => onPageChange(currentPage + 1)}
        disabled={currentPage === totalPages}
        className="px-4 py-2 mx-1 bg-gray-200 hover:bg-gray-300 disabled:opacity-50 rounded"
      >
        Next
      </button>
    </div>
  );
});

Pagination.propTypes = {
  currentPage: PropTypes.number.isRequired,
  totalPages: PropTypes.number.isRequired,
  onPageChange: PropTypes.func.isRequired,
};

const getStatusBadgeProps = (status) => {
  const statusMap = {
    0: { text: "Not Signed", bgColor: "bg-red-100", textColor: "text-red-800" },
    1: {
      text: "Partially Signed",
      bgColor: "bg-yellow-100",
      textColor: "text-yellow-800",
    },
    2: { text: "Signed", bgColor: "bg-green-100", textColor: "text-green-800" },
  };
  return (
    statusMap[status] || {
      text: "Unknown",
      bgColor: "bg-gray-100",
      textColor: "text-gray-800",
    }
  );
};

const getDocumentTypeBadgeProps = (type) => {
  const typeMap = {
    1: { text: "GFE", bgColor: "bg-blue-100", textColor: "text-blue-800" },
    2: { text: "RX", bgColor: "bg-purple-100", textColor: "text-purple-800" },
    3: { text: "Collab", bgColor: "bg-green-100", textColor: "text-green-800" },
    4: {
      text: "Template Sign",
      bgColor: "bg-yellow-100",
      textColor: "text-yellow-800",
    },
    5: {
      text: "Operator Sign",
      bgColor: "bg-indigo-100",
      textColor: "text-indigo-800",
    },
  };
  return (
    typeMap[type] || {
      text: `Type ${type}`,
      bgColor: "bg-gray-100",
      textColor: "text-gray-800",
    }
  );
};

const getIsActiveBadgeProps = (isActive) => {
  return isActive === 1
    ? {
        text: "Activated",
        bgColor: "bg-green-100",
        textColor: "text-green-800",
      }
    : { text: "Deactivated", bgColor: "bg-red-100", textColor: "text-red-800" };
};

const SignedDocumentsPage = () => {
  const navigate = useNavigate();
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [sortColumn, setSortColumn] = useState(null);
  const [sortDirection, setSortDirection] = useState("asc");
  const [documentType, setDocumentType] = useState(1);

  const { data, isLoading, isError, error } = useGetAllSignLinkDocumentsQuery({
    page: currentPage,
    limit: itemsPerPage,
    document_type: documentType,
  });

  const [documents, setDocuments] = useState([]);

  useEffect(() => {
    if (data?.data?.data) {
      setDocuments(data.data.data);
    }
  }, [data]);

  const totalRecords = data?.data?.totalRecords || 0;
  const totalPages = data?.data?.totalPages || 1;

  const sortDocuments = useCallback((docs, column, direction) => {
    if (!column || !docs) return docs ? [...docs] : [];

    return [...docs].sort((a, b) => {
      let valueA = a[column] ?? "";
      let valueB = b[column] ?? "";

      if (column === "date_created") {
        valueA = new Date(valueA).getTime();
        valueB = new Date(valueB).getTime();
      } else if (typeof valueA === "string") {
        valueA = valueA.toLowerCase();
        valueB = valueB.toLowerCase();
      }

      const comparison = valueA < valueB ? -1 : valueA > valueB ? 1 : 0;
      return direction === "asc" ? comparison : -comparison;
    });
  }, []);

  const sortedDocuments = useMemo(
    () => sortDocuments(documents, sortColumn, sortDirection),
    [documents, sortColumn, sortDirection, sortDocuments]
  );

  const handleSort = useCallback(
    (column) => {
      setSortColumn(column);
      setSortDirection((prevDirection) =>
        column === sortColumn
          ? prevDirection === "asc"
            ? "desc"
            : "asc"
          : "asc"
      );
    },
    [sortColumn]
  );

  const handlePageChange = useCallback((newPage) => {
    setCurrentPage(newPage);
  }, []);

  const handleItemsPerPageChange = useCallback((event) => {
    const newItemsPerPage = parseInt(event.target.value, 10);
    setItemsPerPage(newItemsPerPage);
    setCurrentPage(1);
  }, []);

  if (isLoading) {
    return (
      <div className="container mx-auto px-4 py-8 flex justify-center items-center h-screen">
        <ClipLoader color="#007bff" size={40} />
        <span className="ml-2">Loading documents...</span>
      </div>
    );
  }

  if (isError) {
    const errorMessage = (() => {
      try {
        const parsedError = JSON.parse(error?.message);
        const message = parsedError.message || "An unexpected error occurred.";
        if (message === "Invalid or expired token") {
          Cookies.remove("token");
          Cookies.remove("user");
          navigate("/login", { replace: true });
        }
        return message;
      } catch {
        return error?.message || "An unexpected error occurred.";
      }
    })();

    return (
      <div className="container mx-auto px-4 py-8">
        <h1 className="text-2xl font-bold mb-4">My Templates</h1>
        <div className="text-center py-8">
          <h2 className="text-2xl font-semibold text-red-500">
            Error: Could not load templates.
          </h2>
          <p className="text-gray-600">{errorMessage}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-full container mx-auto px-4 py-8">
      <h1 className="text-2xl font-bold mb-4">Signed Documents</h1>
      <div className="overflow-x-auto sm:rounded-lg">
        {sortedDocuments.length === 0 ? (
          <div className="text-center py-8 h-screen">
            <h2 className="text-2xl font-semibold text-gray-700">No results</h2>
          </div>
        ) : (
          <>
            <DocumentTypeFilter
              documentType={documentType}
              setDocumentType={setDocumentType}
            />
            <table className="w-full text-sm text-left text-gray-500 dark:text-gray-400 table-auto">
              <TableHeader
                sortColumn={sortColumn}
                sortDirection={sortDirection}
                handleSort={handleSort}
              />
              <tbody>
                {sortedDocuments.map((document) => (
                  <tr
                    key={document.id}
                    className="bg-white border-b dark:bg-gray-800 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-600"
                  >
                    <td className="py-2 px-3 sm:px-2">
                      {document.qualiphy_clinic_id || "-"}
                    </td>
                    <td className="py-2 px-3 sm:px-2">
                      {document.qualiphy_admin_id || "-"}
                    </td>
                    <td className="py-2 px-3 sm:px-2">
                      {document.qualiphy_user_id || "-"}
                    </td>
                    <td className="py-2 px-3 sm:px-2">
                      {document.is_active !== undefined ? (
                        <Badge {...getIsActiveBadgeProps(document.is_active)} />
                      ) : (
                        "-"
                      )}
                    </td>
                    <td className="py-2 px-3 sm:px-2 whitespace-nowrap dark:text-white">
                      {document.qualiphy_clinic_name || "-"}
                    </td>
                    <td className="py-2 px-3 sm:px-2">
                      {document.document_type !== undefined ? (
                        <Badge
                          {...getDocumentTypeBadgeProps(document.document_type)}
                        />
                      ) : (
                        "-"
                      )}
                    </td>
                    <td className="whitespace-nowrap py-2 px-3 sm:px-2">
                      {document.status !== undefined ? (
                        <Badge {...getStatusBadgeProps(document.status)} />
                      ) : (
                        "-"
                      )}
                    </td>
                    <td className="py-2 px-3 sm:px-2">
                      {document.date_created
                        ? new Date(document.date_created).toLocaleDateString()
                        : "-"}
                    </td>
                    <td className="py-2 px-3 sm:px-2">
                      {document.final_document_url ? (
                        <a
                          href={document.final_document_url}
                          className="text-blue-500 hover:underline"
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          View
                        </a>
                      ) : (
                        "-"
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>

            <div className="flex justify-between items-center mt-4">
              <div className="flex items-center">
                <label htmlFor="itemsPerPage" className="mr-2 text-sm">
                  Items per page
                </label>
                <select
                  id="itemsPerPage"
                  className="appearance-none border rounded px-4 py-2 text-sm pr-8"
                  value={itemsPerPage}
                  onChange={handleItemsPerPageChange}
                >
                  {[5, 10, 20, 50].map((value) => (
                    <option key={value} value={value}>
                      {value}
                    </option>
                  ))}
                </select>
              </div>

              <Pagination
                currentPage={currentPage}
                totalPages={totalPages}
                onPageChange={handlePageChange}
              />
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default memo(SignedDocumentsPage);
